import type React from "react";
import { Box, ButtonBase, Paper, Typography } from "@mui/material";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DetailSectionCard from "../common/DetailSectionCard";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

interface DashboardAttentionSectionProps {
  orders: number;
  standbyOrders: number;
  uangJalanReports: number;
  suratJalan: number;
}

interface AttentionItem {
  labelKey: TranslationKeys;
  count: number;
  to: string;
  testId: string;
}

const DashboardAttentionSection: React.FC<DashboardAttentionSectionProps> = ({
  orders,
  standbyOrders,
  uangJalanReports,
  suratJalan,
}) => {
  const { t } = useTranslation();

  const items: AttentionItem[] = [
    {
      labelKey: "dashboard.attentionOrders",
      count: orders,
      to: "/orders/action-required",
      testId: TestId.DASHBOARD.ATTENTION_ORDERS,
    },
    {
      labelKey: "dashboard.attentionStandby",
      count: standbyOrders,
      to: "/orders/action-required",
      testId: TestId.DASHBOARD.ATTENTION_STANDBY,
    },
    {
      labelKey: "dashboard.attentionReports",
      count: uangJalanReports,
      to: "/uang-jalan-reports/action-required",
      testId: TestId.DASHBOARD.ATTENTION_REPORTS,
    },
    {
      labelKey: "dashboard.attentionSuratJalan",
      count: suratJalan,
      to: "/surat-jalan/action-required",
      testId: TestId.DASHBOARD.ATTENTION_SURAT_JALAN,
    },
  ];

  return (
    <DetailSectionCard
      title={t("dashboard.needsAttention" as TranslationKeys)}
      icon={<AssignmentLateOutlinedIcon fontSize="small" />}
      iconColor="#F59E0B"
      sx={{ mb: 2 }}
      data-testid={TestId.DASHBOARD.ATTENTION}
    >
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        }}
      >
        {items.map((item) => (
          <ButtonBase
            key={item.testId}
            component={RouterLink}
            to={item.to}
            aria-label={`${t(item.labelKey)}, ${item.count}`}
            data-testid={item.testId}
            sx={{
              width: "100%",
              textAlign: "left",
              borderRadius: 2,
              opacity: item.count === 0 ? 0.55 : 1,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                component="p"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
              >
                {item.count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(item.labelKey)}
              </Typography>
            </Paper>
          </ButtonBase>
        ))}
      </Box>
    </DetailSectionCard>
  );
};

export default DashboardAttentionSection;
